/**
 * «Находки» Обзора района (ADR-0014, agroai-6al.43): 3–5 главных находок
 * ночного сканера по слою метрик — по одной от каждого детектора в порядке
 * правил (`docs/model/insights-rules.yaml`), со ссылкой на экран находки.
 *
 * Находка — отклонение или совпадение по метрикам на дату среза, а не
 * причина и не оценка хозяйства: балла нет (журнал №86), числа — из
 * доказательств сервера, клиент ничего не пересчитывает. Остальные находки —
 * раскрытием на месте (подгружаются по просьбе).
 */
import { Link } from 'react-router-dom';
import { Lightbulb } from 'lucide-react';
import { api, type FindingOut, type QueryParams } from '../../../api/client';
import { useRequest } from '../../../api/useRequest';
import { InfoPopover } from '../../../components/InfoPopover';
import { Card } from '../../ui/Card';
import { Disclosure } from '../../ui/Disclosure';
import { EmptyState } from '../../ui/EmptyState';
import { AsyncBlock } from '../../ui/AsyncBlock';
import { SkeletonList } from '../../ui/Skeleton';
import { dateShort, WORDS } from '../../lib/format';
import { defineMessages } from '../../../i18n/define';
import { useT } from '../../../i18n/hooks';
import { tr } from '../../../i18n/runtime';

const m = defineMessages({
  title: { id: 'district.insights.title', defaultMessage: 'Находки', description: 'Заголовок карточки на «Обзоре района» (хоким, менеджер): главные находки ночного сканера по метрикам' },
  scannerAt: { id: 'district.insights.scannerAt', defaultMessage: 'сканер на {date}', description: 'Мелкая подпись справа в заголовке карточки «Находки»: на какую дату среза считал сканер; {date} — готовая дата («26 авг 2026»)' },
  infoLabel: { id: 'district.insights.infoLabel', defaultMessage: 'Как ищутся находки', description: 'aria-label кнопки «i» у заголовка карточки «Находки»' },
  info: {
    id: 'district.insights.info',
    defaultMessage:
      'Ночной сканер проходит слой метрик (те же определения, что у экранов и ИИ) и ищет то, чего нет в «Где нужно разобраться»: хозяйства резко хуже ровесников (медиана и разброс группы, один огромный не сдвигает), совпадения на одних хозяйствах (недоподача воды и поливы без отчёта, красный карантин и защита растений, крупный план и сдача), резкую смену темпа неделя к неделе и рост за четыре недели. С прошлыми сезонами сканер не сравнивает: у каждого года свои планы, вода и условия. Здесь — первая находка каждого вида. Это совпадения и отклонения, а не причины и не оценка хозяйства: балла нет. Фильтр массива к находкам не применяется — сканер считает район целиком.',
    description: 'Текст подсказки «i» у заголовка карточки «Находки» (хоким, менеджер): как работает ночной сканер и чего он не утверждает; длинный абзац во всплывающем окне',
  },
  restAll: { id: 'district.insights.restAll', defaultMessage: 'все находки', description: 'Что загружается (винительный падеж, строчными): подставляется в «Не удалось загрузить {what}» при ошибке списка всех находок' },
  restSkeleton: { id: 'district.insights.restSkeleton', defaultMessage: 'находки', description: 'Подпись заглушки загрузки списка находок для чтеца с экрана, строчными' },
  headSkeleton: { id: 'district.insights.headSkeleton', defaultMessage: 'находки сканера', description: 'Подпись заглушки загрузки главных находок для чтеца с экрана и «Не удалось загрузить {what}»; строчными, винительный падеж' },
  noRunTitle: { id: 'district.insights.noRun.title', defaultMessage: 'Сканер на этот срез не запускался', description: 'Заголовок пустого состояния карточки «Находки»: для выбранной даты среза прогона сканера нет' },
  noRunReason: { id: 'district.insights.noRun.reason', defaultMessage: 'Находок нет потому, что прогона нет, а не потому, что всё хорошо.', description: 'Пояснение под заголовком пустого состояния «Сканер на этот срез не запускался»' },
  noneTitle: { id: 'district.insights.none.title', defaultMessage: 'Находок нет', description: 'Заголовок пустого состояния карточки «Находки»: сканер отработал, находок не нашёл' },
  noneReason: { id: 'district.insights.none.reason', defaultMessage: 'На {date} ни одно отклонение и ни одна связь не дошли до порогов правил сканера.', description: 'Пояснение в пустом состоянии «Находок нет»; {date} — готовая дата среза («26 авг 2026»)' },
  disclosureLabel: { id: 'district.insights.disclosure.label', defaultMessage: 'Все находки', description: 'Заголовок раскрывающегося блока под списком главных находок (все остальные находки)' },
  disclosureSummary: {
    id: 'district.insights.disclosure.summary',
    defaultMessage: 'ещё {count, plural, one {# находка} few {# находки} many {# находок} other {# находки}}',
    description: 'Краткая подпись свёрнутого блока «Все находки»: сколько находок скрыто; {count} — число',
  },
});

/**
 * Сервер пишет «264 хоз.» — на экране сокращение читается машинным текстом (O7):
 * «хоз.» после числа раскрывается по числу. Остальное — как пришло с сервера.
 */
export function plainFinding(text: string): string {
  return text.replace(/(\d+)(\s|\u00a0)+хоз\./g, (_, n: string) => tr(WORDS.farm, { count: Number(n) }));
}

function FindingRow({ item, linkSearch }: { item: FindingOut; linkSearch: () => string }) {
  return (
    <li className="district-insight">
      <span className="district-insight__kind">{item.detector_title}</span>
      <Link className="district-insight__title" to={`${item.href}${linkSearch()}`}>{plainFinding(item.title)}</Link>
    </li>
  );
}

function RestOfFindings({ params, ctxKey, skip, linkSearch }: { params: QueryParams; ctxKey: string; skip: Set<number>; linkSearch: () => string }) {
  const t = useT();
  const all = useRequest(() => api.insights({ ...params, limit: 200 }), [ctxKey]);
  return (
    <AsyncBlock req={all} what={t(m.restAll)} skeleton={<SkeletonList label={t(m.restSkeleton)} items={4} tone="soft" />}>
      {data => (
        <ul className="district-insights">
          {data.findings.filter(f => !skip.has(f.id)).map(f => <FindingRow key={f.id} item={f} linkSearch={linkSearch} />)}
        </ul>
      )}
    </AsyncBlock>
  );
}

export function InsightsBlock({ params, ctxKey, linkSearch }: { params: QueryParams; ctxKey: string; linkSearch: () => string }) {
  const t = useT();
  const head = useRequest(() => api.insights({ ...params, headline: true }), [ctxKey]);
  const data = head.data;
  const run = data?.run ?? null;
  const shown = data?.findings ?? [];
  const more = data ? Math.max(0, data.total - shown.length) : 0;
  return (
    <Card
      title={t(m.title)}
      aside={
        <span className="district-chart__aside">
          {run ? <span className="district-foot">{t(m.scannerAt, { date: dateShort(run.as_of) })}</span> : null}
          <InfoPopover icon="info" label={t(m.infoLabel)}>
            {t(m.info)}
          </InfoPopover>
        </span>
      }
    >
      <AsyncBlock req={head} what={t(m.headSkeleton)} skeleton={<SkeletonList label={t(m.headSkeleton)} items={5} tone="soft" />}>{() => !run ? (
        <EmptyState compact icon={Lightbulb} title={t(m.noRunTitle)} reason={t(m.noRunReason)} />
      ) : shown.length === 0 ? (
        <EmptyState compact icon={Lightbulb} title={t(m.noneTitle)} reason={t(m.noneReason, { date: dateShort(run.as_of) })} />
      ) : (
        <>
          <ul className="district-insights">
            {shown.map(f => <FindingRow key={f.id} item={f} linkSearch={linkSearch} />)}
          </ul>
          {more > 0 ? (
            <Disclosure label={t(m.disclosureLabel)} summary={t(m.disclosureSummary, { count: more })}>
              <RestOfFindings params={params} ctxKey={ctxKey} skip={new Set(shown.map(f => f.id))} linkSearch={linkSearch} />
            </Disclosure>
          ) : null}
        </>
      )}</AsyncBlock>
    </Card>
  );
}
